/* eslint-disable react/jsx-props-no-spreading */
import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useMutation } from '@tanstack/react-query';
import {
  Message, Button, MediaCard, Skeleton,
} from '@zolteam/zol-admin-ui';
import { useTranslation } from 'react-i18next';

// Styles
import styles from './UploadFile.module.css';

// Service
import { createFile } from '../../../services/files/filesService';

/**
 * uplaod file component allows users to drag and drop or select files for upload.
 * @param {object} props - Component props
 * @param {(file: File[], fileUuid: string) => void} [props.onDrop=() => {}]
 * @param {boolean} [props.hasFile=false] - Flag indicating whether a file has been uploaded
 * @param {string|React.ReactNode} [props.noFileChildren='']
 * @param {string | File} [props.file='']
 * @param {number} [props.index=0]
 * @returns {JSX.Element} The rendered UploadMedia component
 */
function UploadFile({
  onDrop = () => {}, hasFile = false, noFileChildren = '', file = '', index = 0,
}) {
  // hooks
  const { t } = useTranslation();
  // local state
  const [uploadError, setUploadError] = useState(null);

  // mutations
  const {
    mutate,
    isLoading,
  } = useMutation(createFile);

  /**
   * Handle file drop
   * @param {File[]} files - The files to upload
   * @returns {void}
   */
  const handleDrop = (files) => {
    mutate(files[0], {
      onSuccess: (data) => {
        setUploadError(false);
        onDrop(files, data.fileUuid);
      },
      onError: () => {
        setUploadError(true);
        onDrop(null, null);
      },
    });
  };

  // hooks
  const { getRootProps, getInputProps } = useDropzone({ onDrop: handleDrop, accept: { 'image/*': [], 'application/*': [], 'text/*': [] } });

  const renderPreview = () => {
    if (typeof file === 'string') {
      return null;
    }
    if (!file?.type?.includes('image')) {
      const mediaCard = document.querySelector(`.media-card${index} .ui-mediacard__preview`);
      const infoTag = document.querySelector(`.media-card${index} .ui-mediacard__info__tag`);
      if (mediaCard) {
        // @ts-ignore
        mediaCard.style.display = 'none';
        // @ts-ignore
        infoTag.style.marginRight = '40px';
      }
    }
    return (
      <MediaCard
        className={`media-card${index}`}
        alt={file.name}
        label={file.name}
        mediaType={file.type}
        isPreview
        source={URL.createObjectURL(file)}
        actions={(
          <Button
            color="neutral00"
            icon="close-square-bold"
            onClick={() => { onDrop(null, null); }}
          />
)}
      />
    );
  };

  if (isLoading) {
    return <Skeleton height="200px" width="100%" shape="rectangle" />;
  }
  return (
    <div className={`${styles.wrapper} upload-media_wrapper`}>
      <div className={hasFile ? styles['has-file'] : styles['has-no-file']}>
        {hasFile && renderPreview()}
      </div>
      <div className={hasFile ? styles['has-no-file'] : styles['has-file']}>
        {uploadError
          ? (
            <Message>
              <span className="p2-default">{t('documents.uploadError')}</span>
            </Message>
          ) : null}
        <div className={`${styles.container} upload-media_container`} {...getRootProps()}>
          <input {...getInputProps()} />
          <div className={`${styles['upload-zone']} upload-media_zone`}>
            {noFileChildren}
          </div>
        </div>
      </div>
    </div>
  );
}

export default UploadFile;
